import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import CoinItem from '../../components/common/CoinItem';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import EmptyState from '../../components/common/EmptyState';
// Organization features temporarily disabled
// import SendOrganizationalCoin from '../../components/features/SendOrganizationalCoin';
// import OrganizationCoinsCard from '../../components/features/OrganizationCoinsCard';
import { formatRelativeTime } from '../../utils/dateUtils';
import { calculateCoinTotals } from '../../utils/coinUtils';
import './Home.css';

function Home() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [coinsToShow, setCoinsToShow] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;

        // Fetch all data in parallel
        const [statsData, balancesData, transactionsData, invitesData, sentData, userData] = await Promise.all([
          apiService.getUserStats(idToken),
          apiService.getUserBalances(idToken),
          apiService.getTransactions(idToken, { type: 'received', limit: coinsToShow }),
          apiService.getMyOrganizationInvites(idToken),
          apiService.getTransactions(idToken, { type: 'sent', limit: 50 }),
          apiService.getCurrentUser(idToken)
        ]);

        setStats(statsData.stats);
        setBalances(balancesData.balances || []);
        setTransactions(transactionsData.transactions || []);
        setPendingInvites(invitesData.invites || []);
        setCurrentUser(userData);

        // Extract pending approvals (where current user is sender and tx is pending)
        const sent = sentData.transactions || [];
        const pendingRequests = sent.filter(tx => tx.pending === true || tx.pending === 1);
        setPendingApprovals(pendingRequests);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth.isAuthenticated, auth.user, coinsToShow]);

  // Calculate coin totals from balances
  const coinTotals = balances && balances.length > 0
    ? calculateCoinTotals(balances)
    : {
        individualCoins: 0,
        organizationalCoins: 0,
        coinsSent: stats?.coins_sent || 0,
        totalCoins: 0
      };

  // Use coin totals directly (no need for Cognito user data)
  const userData = {
    ...coinTotals
  };

  // Calculate net coins (received - sent)
  // const totalReceived = userData.individualCoins + userData.organizationalCoins;
  // const netCoins = totalReceived - userData.coinsSent;

  // Transform transactions data for display, filtering out pending transactions
  const latestCoins = transactions
    .filter(tx => !tx.pending) // Exclude pending transactions from Recent Recognition
    .map(tx => ({
      id: tx.tx_id,
      type: tx.coin_type === 'individual' ? 'Individual' : 'Organizational',
      sender: {
        id: tx.sender_id,
        name: tx.sender_name || 'Unknown',
        email: tx.sender_email,
        jobTitle: tx.sender_job_title
      },
      message: tx.description || 'No description',
      timestamp: formatRelativeTime(tx.created_at),
      coinName: tx.coin_type
    }));

  // Handle accepting an organization invite
  const handleAcceptInvite = async (inviteId, organizationId, organizationSlug) => {
    try {
      const idToken = auth.user.id_token;
      await apiService.acceptOrganizationInvite(idToken, inviteId);

      // Remove the invite from the list
      setPendingInvites(prevInvites => prevInvites.filter(inv => inv.invite_id !== inviteId));

      // Navigate to the organization page (prefer slug over UUID)
      const orgIdentifier = organizationSlug || organizationId;
      navigate(`/organization/${orgIdentifier}`);
    } catch (err) {
      console.error('Error accepting invite:', err);
      alert('Failed to accept invitation: ' + err.message);
    }
  };

  // Handle declining an organization invite
  const handleDeclineInvite = async (inviteId) => {
    try {
      const idToken = auth.user.id_token;
      await apiService.declineOrganizationInvite(idToken, inviteId);

      // Remove the invite from the list
      setPendingInvites(prevInvites => prevInvites.filter(inv => inv.invite_id !== inviteId));
    } catch (err) {
      console.error('Error declining invite:', err);
      alert('Failed to decline invitation: ' + err.message);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <main className="main-content">
        <LoadingSpinner message="Loading your dashboard..." />
      </main>
    );
  }

  // Show error state
  if (error) {
    return (
      <main className="main-content">
        <ErrorDisplay
          title="Error Loading Data"
          message={error}
          onRetry={() => window.location.reload()}
        />
      </main>
    );
  }

  return (
    <main className="main-content">
        {/* Organization features temporarily disabled */}
        {/* Send Organizational Coin (Organization Users Only) */}
        {/* {currentUser?.user_type === 'organization' && (
          <SendOrganizationalCoin
            organization={currentUser}
            onSuccess={async () => {
              // Refresh pending approvals after sending a coin
              const idToken = auth.user.id_token;
              const sentData = await apiService.getTransactions(idToken, { type: 'sent', limit: 50 });
              const sent = sentData.transactions || [];
              const pendingRequests = sent.filter(tx => tx.pending === true || tx.pending === 1);
              setPendingApprovals(pendingRequests);
            }}
          />
        )} */}

        {/* Organization Coins Card (Organization Users Only) */}
        {/* {currentUser?.user_type === 'organization' && (
          <OrganizationCoinsCard limit={10} />
        )} */}

        {/* Pending Organization Invitations */}
        {/* {pendingInvites.length > 0 && (
          <section className="pending-invites-section">
            <h2 className="invites-title">Organization Invitations</h2>
            <div className="invites-list">
              {pendingInvites.map(invite => (
                <div key={invite.invite_id} className="invite-card">
                  <div className="invite-header">
                    <div className="invite-org-avatar">
                      {invite.organization_name?.charAt(0)?.toUpperCase() || 'O'}
                    </div>
                    <div className="invite-org-info">
                      <h3 className="invite-org-name">{invite.organization_name}</h3>
                      <p className="invite-org-email">{invite.organization_email}</p>
                    </div>
                  </div>
                  <div className="invite-details">
                    <p className="invite-from">
                      Invited by <strong>{invite.invited_by_name}</strong>
                    </p>
                    {(invite.is_admin === 1 || invite.is_admin === true) && (
                      <span className="invite-admin-badge">As Admin</span>
                    )}
                    <p className="invite-timestamp">{formatRelativeTime(invite.created_at)}</p>
                  </div>
                  <div className="invite-actions">
                    <button
                      className="accept-invite-btn"
                      onClick={() => handleAcceptInvite(invite.invite_id, invite.organization_id, invite.organization_slug)}
                    >
                      Accept
                    </button>
                    <button
                      className="decline-invite-btn"
                      onClick={() => handleDeclineInvite(invite.invite_id)}
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )} */}

        {/* Coin Count Summary and Request Coin - Hide for organizations */}
        {currentUser?.user_type !== 'organization' && (
          <section className="coins-summary">
            <div
              className="coin-card individual clickable-card"
              onClick={() => navigate(`/profile/${auth.user.profile.sub}/coins`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/profile/${auth.user.profile.sub}/coins`);
                }
              }}
              title="View all your coins"
            >
              <div className="coin-card-header">
                <span className="coin-icon">🪙</span>
                <h3>My Coins</h3>
              </div>
              <p className="coin-description">
                {userData.totalCoins} coin{userData.totalCoins !== 1 ? 's' : ''} received
              </p>
            </div>

            <div
              className="coin-card request-card clickable-card"
              onClick={() => navigate('/request-coin')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/request-coin');
                }
              }}
              title="Request recognition for your contributions"
            >
              <div className="coin-card-header">
                <span className="coin-icon">📥</span>
                <h3>Request a Coin</h3>
              </div>
              <p className="coin-description">
                Request recognition for your contributions
              </p>
            </div>

            <div
              className="coin-card pending-requests-card clickable-card"
              onClick={() => navigate('/pending-requests')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate('/pending-requests');
                }
              }}
              title="View pending coin requests"
            >
              <div className="coin-card-header">
                <span className="coin-icon">⏳</span>
                <h3>Pending Requests</h3>
              </div>
              <p className="coin-description">
                {pendingApprovals.length} pending request{pendingApprovals.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Organizational Coins card - temporarily disabled */}
            {/* <div
              className="coin-card organizational clickable-card"
              onClick={() => navigate(`/profile/${auth.user.profile.sub}/coins?type=organizational`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/profile/${auth.user.profile.sub}/coins?type=organizational`);
                }
              }}
              title="View all organizational coins"
            >
              <div className="coin-card-header">
                <span className="coin-icon">&#127942;</span>
                <h3>Organizational Coins</h3>
              </div>
              <div className="coin-count">{userData.organizationalCoins}</div>
              <p className="coin-description">
                Recognition from teams and departments for collaborative work
              </p>
            </div> */}

            {/* <div className="coin-card net">
              <div className="coin-card-header">
                <span className="coin-icon">&#128200;</span>
                <h3>Net Coins</h3>
              </div>
              <div className="coin-count">{netCoins}</div>
              <p className="coin-description">
                Total coins earned after giving recognition to others
              </p>
              <div className="coin-card-footer">
                <span className="coin-stats">
                  Received: {totalReceived} | Sent: {userData.coinsSent}
                </span>
              </div>
            </div> */}
          </section>
        )}

        {/* Latest Coins Awarded - Hide for organizations */}
        {currentUser?.user_type !== 'organization' && (
          <section className="latest-coins">
            <div className="section-header">
              <h2>Recent Recognition</h2>
              <div className="coins-controls">
                <label htmlFor="coins-limit">Show:</label>
                <select
                  id="coins-limit"
                  value={coinsToShow}
                  onChange={(e) => setCoinsToShow(Number(e.target.value))}
                  className="coins-select"
                >
                  <option value={3}>Last 3</option>
                  <option value={5}>Last 5</option>
                  <option value={7}>Last 7</option>
                  <option value={10}>Last 10</option>
                </select>
                <button
                  onClick={() => navigate(`/profile/${auth.user.profile.sub}/coins`)}
                  className="view-all-coins-button"
                >
                  View All Coins
                </button>
              </div>
            </div>

            <div className="coins-list">
              {latestCoins.slice(0, coinsToShow).map((coin) => (
                <CoinItem
                  key={coin.id}
                  id={coin.id}
                  coinType={coin.type.toLowerCase()}
                  senderName={coin.sender.name}
                  senderId={coin.sender.id}
                  message={coin.message}
                  timestamp={coin.timestamp}
                  senderEmail={coin.sender.email}
                  senderJobTitle={coin.sender.jobTitle}
                  direction="received"
                />
              ))}
            </div>

            {latestCoins.length === 0 && (
              <EmptyState
                icon="🎉"
                message="No coins received yet. Keep up the great work!"
              />
            )}
          </section>
        )}
    </main>
  );
}

export default Home;
